import { Badge } from "@/components/ui/badge"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Button } from "@/components/ui/button"
import { Check, Clock, X, Edit, PlusCircle } from "lucide-react"
import { Skeleton } from "@/components/ui/skeleton"
import { FC, useEffect, useState } from "react"
import axios from "axios"
import { BASE_URL } from "@/config"

interface Props {
    id: number
}

const OrderTimeline: FC<Props> = ({ id }) => {

    const [activities, setActivities] = useState<any>();
    const [isLoading, setIsLoading] = useState(true);

    const fetchTimeline = async () => {
        try {
            const response = await axios.get(`${BASE_URL}/taxi-orders/history/timeline/${id}`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem("access_token")}`,
                },
            });
            setActivities(response.data);
        } catch (error) {
            console.error("Error fetching timeline:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const [users, setUsers] = useState<any[]>([]);

    const fetchUsers = async () => {
        try {
            const response = await axios.get(`${BASE_URL}/users`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem("access_token")}`,
                },
            });
            setUsers(response.data);
        } catch (error) {
            console.error("Error fetching users:", error);
        }
    };

    useEffect(() => {
        fetchTimeline();
        fetchUsers();
    }, []);

    const getStatusContent: any = (status: any, userId: any, users: any[]) => {

        const matchedUser = users.find((u) => u.id === userId);
        const fullName = matchedUser?.firstname && matchedUser?.lastname
            ? `${matchedUser.firstname} ${matchedUser.lastname}`
            : "Website User";

        switch (status) {
            case "NEW_ORDER":
                return {
                    icon: <PlusCircle className="h-4 w-4 text-blue-500" />,
                    text: `${fullName} has placed a new taxi order. The request is now pending review.`,
                    badgeVariant: "outline",
                };
            case "ACCEPTED":
                return {
                    icon: <Check className="h-4 w-4 text-green-500" />,
                    text: `${fullName} has reviewed and accepted the order. It will be processed shortly.`,
                    badgeVariant: "outline",
                };
            case "REJECTED":
                return {
                    icon: <X className="h-4 w-4 text-red-500" />,
                    text: `${fullName} has reviewed and rejected the order. No further action will be taken.`,
                    badgeVariant: "outline",
                };
            case "UPDATED":
                return {
                    icon: <Edit className="h-4 w-4 text-yellow-500" />,
                    text: `${fullName} has made changes to the order details. Please review the updated information.`,
                    badgeVariant: "outline",
                };
            default:
                return {
                    icon: <Clock className="h-4 w-4 text-gray-500" />,
                    text: `${fullName} performed an update or action on this order.`,
                    badgeVariant: "outline",
                };
        }
    };

    const TimelineSkeleton = () => {
        return (
            <div className="space-y-6 relative border-l border-gray-200 pl-6">
                {[...Array(4)].map((_, i) => (
                    <div key={i} className="relative">
                        <div className="absolute -left-3 top-1">
                            <div className="bg-white rounded-full border shadow p-1">
                                <Skeleton className="h-4 w-4 rounded-full" />
                            </div>
                        </div>
                        <div className="ml-6 space-y-2">
                            <Skeleton className="h-4 w-2/3" />
                            <Skeleton className="h-3 w-full" />
                            <div className="flex gap-2">
                                <Skeleton className="h-4 w-20 rounded-full" />
                                <Skeleton className="h-4 w-24" />
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    return (
        <div className="w-full rounded-md py-4">
            <h2 className="text-lg font-semibold mb-4">Order Activity</h2>
            {isLoading ? (
                <TimelineSkeleton />
            ) : (
                <div className="max-h-[650px] overflow-auto space-y-6 relative border-l border-gray-200 px-6">
                    {activities?.length > 0 ? (
                        activities.map((event: any) => {
                            const { icon, text, badgeVariant } = getStatusContent(event.status, event.user_id, users);

                            return (
                                <div key={event.id} className="relative pb-2">
                                    <div className="absolute -left-3 top-1">
                                        <div className="bg-white rounded-full border shadow p-1">
                                            {icon}
                                        </div>
                                    </div>
                                    <div className="ml-6">
                                        <p className="text-sm">{text}</p>
                                        <div className="mt-2 flex items-center gap-2 text-xs text-gray-500">
                                            {event.status && (
                                                <Badge variant={badgeVariant}>{event.status}</Badge>
                                            )}
                                            <span>
                                                {new Date(event?.created_at).toLocaleString("en-GB", {
                                                    day: "2-digit",
                                                    month: "2-digit",
                                                    year: "numeric",
                                                    hour: "2-digit",
                                                    minute: "2-digit"
                                                })}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            );
                        })
                    ) : (
                        <div className="flex items-center justify-center min-h-[200px] text-center">
                            <p className="text-sm">No results.</p>
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}

export default OrderTimeline;